import { useMediaQuery } from "@mui/material";
import { useCallback, useMemo, useState } from "react";

import ApiRequestErrorHandler from "../../../../../global/ApiRequestErrorHandler";
import ProviderDetail from "../../../../../modules/providers/provider-detail";
import HNSColors from "../../../../../ui library/HNSColors";
import EditUploadDocument from "../../../../Documents/EditUploadDocument";
import { ACTION_TYPE } from "../../utils/strings";

import ActionIconButton from "./ActionIconButton";
import ActionsMenu from "./ActionsMenu";
import actionButtonsStyles, { VIEW_CONFIG_BY_VARIANT } from "./styles/actionButtonsStyles";
import { buildCallActions, buildDetailActions, resolvePhones } from "./utils/actionButtonsHelpers";

const ActionButtons = ({
  formHeader,
  caseId,
  actionItem,
  historyNavigate,
  providerRepository,
  dialClient,
  setErrorMessage,
  setOpenError,
  onDocumentUpdated = () => {},
}) => {
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [providerDetail, setProviderDetail] = useState();
  const [editDocumentModalOpen, setEditDocumentModalOpen] = useState(false);
  const [callMenuAnchor, setCallMenuAnchor] = useState(null);
  const [viewMenuAnchor, setViewMenuAnchor] = useState(null);
  const isNarrowScreen = useMediaQuery("(max-width:900px)");
  const headerType = formHeader?.header_type ?? "default_action_item_header";
  const actionType = parseInt(formHeader?.action_type, 10);
  const resolvedCaseId = formHeader?.case_id ?? caseId;
  const selectedDocument = useMemo(() => {
    if (!formHeader?.document_id) {
      return null;
    }
    return {
      id: formHeader.document_id,
      fileName: formHeader?.file_name,
    };
  }, [formHeader]);
  const documentPrefillOverrides = useMemo(() => {
    if (headerType !== "document_focused_header" || !formHeader) {
      return null;
    }

    const buildOverride = (id, label) => {
      const normalizedId = id ?? null;
      const normalizedLabel = label ?? null;
      if (normalizedId === null && normalizedLabel === null) {
        return null;
      }
      return { id: normalizedId, label: normalizedLabel };
    };

    const referralFromId = formHeader?.referred_from_id ?? formHeader?.referral_from_id ?? null;
    const referralToId = formHeader?.referred_to_id ?? formHeader?.referral_to_id ?? null;
    const sourceId = formHeader?.document_source ?? formHeader?.document_source_id ?? null;
    const documentTypeId = formHeader?.document_type ?? formHeader?.document_type_id ?? null;

    const overrides = {
      documentType: buildOverride(documentTypeId, formHeader?.document_type_label),
      source: buildOverride(sourceId, formHeader?.document_source_label),
      provider: buildOverride(formHeader?.provider_id, formHeader?.provider_name),
      referralFrom: buildOverride(
        referralFromId,
        formHeader?.referred_from_name ?? formHeader?.referral_from_name
      ),
      referralTo: buildOverride(
        referralToId,
        formHeader?.referred_to_name ?? formHeader?.referral_to_name
      ),
    };

    const hasAnyOverride = Object.values(overrides).some(Boolean);
    return hasAnyOverride ? overrides : null;
  }, [formHeader, headerType]);

  const handleCallClick = useCallback(
    (number) => {
      if (!number) {
        return;
      }
      dialClient(number);
    },
    [dialClient]
  );

  const handleViewProviderBtnClick = useCallback(async () => {
    const providerId = formHeader?.provider_id;
    if (!providerId) {
      return;
    }
    try {
      const provider = await providerRepository.findRecord(providerId);
      setProviderDetail(provider);
      setProviderModalOpen(true);
    } catch (error) {
      const errArr = ApiRequestErrorHandler(error);
      setErrorMessage(errArr);
      setOpenError(true);
    }
  }, [formHeader?.provider_id, providerRepository, setErrorMessage, setOpenError]);

  const handleOpenEditDocument = useCallback(() => {
    if (!formHeader?.document_id) {
      return;
    }
    setEditDocumentModalOpen(true);
  }, [formHeader?.document_id]);

  const handleCloseEditDocument = () => setEditDocumentModalOpen(false);

  const callButtonSx = actionButtonsStyles.callButton(isNarrowScreen);
  const viewButtonSx = actionButtonsStyles.viewButton(isNarrowScreen);
  const menuPaperSx = actionButtonsStyles.menuPaper(isNarrowScreen);
  const menuItemsSx = actionButtonsStyles.menuItem;

  const isDataEntryFollowUp = actionType === ACTION_TYPE.DATA_ENTRY_FOLLOW_UP;
  const phones = useMemo(() => resolvePhones({ formHeader, actionItem }), [formHeader, actionItem]);
  const callActions = useMemo(
    () =>
      buildCallActions({
        isDataEntryFollowUp,
        headerType,
        phones,
        onCall: handleCallClick,
      }),
    [handleCallClick, headerType, isDataEntryFollowUp, phones]
  );
  const { clientPhone } = phones;

  const detailActions = useMemo(
    () =>
      buildDetailActions({
        isDataEntryFollowUp,
        resolvedCaseId,
        headerType,
        formHeader,
        onClientDetails: () => historyNavigate("client-detail", resolvedCaseId),
        onProviderDetails: handleViewProviderBtnClick,
        onEditDocument: handleOpenEditDocument,
      }),
    [
      formHeader,
      handleOpenEditDocument,
      handleViewProviderBtnClick,
      headerType,
      historyNavigate,
      isDataEntryFollowUp,
      resolvedCaseId,
    ]
  );

  let headerVariant = "default";
  if (headerType === "client_focused_header") {
    headerVariant = "client";
  } else if (headerType === "law_firm_focused_header") {
    headerVariant = "lawFirm";
  }

  const viewConfig = VIEW_CONFIG_BY_VARIANT[headerVariant];
  const isViewDisabled = !resolvedCaseId;
  const viewIconColor = isViewDisabled ? HNSColors.neutrals[500] : HNSColors.neutrals[900];
  const onViewClientDetails = useCallback(
    () => historyNavigate("client-detail", resolvedCaseId),
    [historyNavigate, resolvedCaseId]
  );

  return (
    <>
      <div className="flex flex-wrap items-center justify-end gap-3 md:justify-end">
        {headerVariant === "default" ? (
          <ActionsMenu
            label="View Details"
            menuId="view-actions-menu"
            buttonSx={viewButtonSx}
            menuPaperSx={menuPaperSx}
            menuItemsSx={menuItemsSx}
            actions={detailActions}
            anchorEl={viewMenuAnchor}
            setAnchorEl={setViewMenuAnchor}
            buttonClassName="inline-flex items-center justify-center gap-2 rounded-[500px] normal-case leading-none tracking-normal text-white !shadow-none"
          />
        ) : (
          <ActionIconButton
            label="View Client Details"
            sx={viewButtonSx}
            className={viewConfig.className}
            onClick={onViewClientDetails}
            disabled={isViewDisabled}
            iconName={viewConfig.iconName}
            iconSx={viewConfig.iconSx}
            iconColor={viewIconColor}
          />
        )}

        <div>
          {headerVariant === "client" ? (
            <ActionIconButton
              label="Call Client"
              sx={callButtonSx}
              className="inline-flex items-center justify-between rounded-[500px] px-[18px] py-[10px] text-center font-['Roboto'] font-semibold normal-case leading-none tracking-normal text-white !shadow-none"
              onClick={() => handleCallClick(clientPhone)}
              disabled={!clientPhone}
              iconColor={clientPhone ? HNSColors.neutrals[900] : HNSColors.neutrals[500]}
              iconName="his-phone-outgoing"
              iconSx={{ width: "22px" }}
            />
          ) : (
            <ActionsMenu
              label="Call"
              menuId="call-actions-menu"
              buttonSx={callButtonSx}
              menuPaperSx={menuPaperSx}
              menuItemsSx={menuItemsSx}
              actions={callActions}
              anchorEl={callMenuAnchor}
              setAnchorEl={setCallMenuAnchor}
              buttonClassName="inline-flex items-center justify-between rounded-[500px] px-[18px] py-[10px] text-center font-['Roboto'] normal-case leading-none tracking-normal text-white !shadow-none"
            />
          )}
        </div>

        {providerModalOpen && (
          <ProviderDetail
            providerDetail={providerDetail}
            open={providerModalOpen}
            setOpen={setProviderModalOpen}
          />
        )}
      </div>
      {headerType === "document_focused_header" && editDocumentModalOpen && selectedDocument && (
        <EditUploadDocument
          open
          onClose={handleCloseEditDocument}
          selectedDocument={selectedDocument}
          initialSelectedDocument={selectedDocument}
          title="Edit Document"
          prefillOverrides={documentPrefillOverrides}
          shouldHydrateSelectedDocument
          externalRefresh={onDocumentUpdated}
        />
      )}
    </>
  );
};

export default ActionButtons;
